import { EProductTourType, EFeatures, EProductTourStatus, EProductTourTargets } from '@constants';
import { TourStepProps } from 'antd';
import Lottie from 'lottie-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useProfile } from '../profile';

import { useMutateProductTour } from './useMutateProductTour';
import { useUserProductTour } from './useUserProductTour';

import swipeHandAnimation from '@/assets/swipe-hand.json';
import UMY_WELCOME from '@/assets/umy_welcome.svg';
import { useIsMobile, useTagFeatures } from '@/hooks';
import { ESidebarTabs } from '@/pages/Student/Course/constants';
import { ECourseDetailTabs } from '@/types';

type ProductTourStep = TourStepProps & { availableForFeature?: EFeatures; checkInDOM?: boolean };

type IProductTourSteps = Partial<{
  [pagePathname: string]: {
    tourType: EProductTourType;
    steps: ProductTourStep[];
  };
}>;
const DELAY = 200;

export const useProductTour = () => {
  const { t } = useTranslation('ProductTourTranslations');
  const { productTour } = useUserProductTour();
  const { updateProductTour } = useMutateProductTour();
  const { canRender } = useTagFeatures();
  const { pathname, search } = useLocation();
  const navigateTo = useNavigate();
  const isMobile = useIsMobile('md');
  const {
    roleCheck: { isTeacher, isManager, isStudent }
  } = useProfile();

  const unfinishedTours = useMemo(() => {
    if (!productTour) return [];
    return Object.entries(productTour.tours).reduce<EProductTourType[]>((acc, [tourType, tour]) => {
      if (tour.status === EProductTourStatus.NOT_STARTED) {
        acc.push(tourType as EProductTourType);
      }
      return acc;
    }, []);
  }, [productTour]);

  const handleActivitiesNextStep = (tabKey: string) => {
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const currentHash = window.location.hash;

    const params = new URLSearchParams(currentSearch);
    if (params.get('activeTab') === tabKey) return;
    params.set('activeTab', tabKey);
    navigateTo(
      { pathname: currentPath, search: `?${params.toString()}`, hash: currentHash },
      { replace: true }
    );
  };

  const tourSteps: IProductTourSteps = useMemo(() => {
    if (isStudent) {
      return {
        '/dashboard': {
          tourType: EProductTourType.DASHBOARD_PRODUCT_TOUR,
          steps: [
            {
              title: t('PROGRESS_WIDGET_PRODUCT_TOUR_TITLE'),
              description: t('PROGRESS_WIDGET_PRODUCT_TOUR_DESCRIPTION'),
              prevButtonProps: { children: t('SKIP_THE_TOUR') },
              nextButtonProps: { children: t('NEXT') },
              target: () => document.getElementById(EProductTourTargets.PROGRESS_WIDGET)!
            },
            {
              title: t('SUBSCRIPTIONS_WIDGET_PRODUCT_TOUR_TITLE'),
              description: t('SUBSCRIPTIONS_WIDGET_PRODUCT_TOUR_DESCRIPTION'),
              prevButtonProps: { children: t('PREVIOUS') },
              nextButtonProps: { children: t('NEXT') },
              target: () => document.getElementById(EProductTourTargets.SUBSCRIPTIONS_WIDGET)!,
              checkInDOM: true
            },
            {
              title: t('EXAM_GROUPS_WIDGET_PRODUCT_TOUR_TITLE'),
              description: t('EXAM_GROUPS_WIDGET_PRODUCT_TOUR_DESCRIPTION'),
              prevButtonProps: { children: t('PREVIOUS') },
              nextButtonProps: { children: t('NEXT') },
              availableForFeature: EFeatures.ANNOUNCEMENTS,
              target: () => document.getElementById(EProductTourTargets.EXAM_GROUPS_WIDGET)!
            },
            {
              title: t('POSTS_WIDGET_PRODUCT_TOUR_TITLE'),
              description: t('POSTS_WIDGET_PRODUCT_TOUR_DESCRIPTION'),
              prevButtonProps: { children: t('PREVIOUS') },
              nextButtonProps: { children: t('NEXT') },
              availableForFeature: EFeatures.ANNOUNCEMENTS,
              target: () => document.getElementById(EProductTourTargets.POSTS_WIDGET)!
            },
            {
              title: t('RANKINGS_WIDGET_PRODUCT_TOUR_TITLE'),
              description: t('RANKINGS_WIDGET_PRODUCT_TOUR_DESCRIPTION'),
              prevButtonProps: { children: t('PREVIOUS') },
              nextButtonProps: { children: t('NEXT') },
              availableForFeature: EFeatures.QUIZZES,
              target: () => document.getElementById(EProductTourTargets.RANKINGS_WIDGET)!
            },
            {
              title: t('FIRST_STEPS_PRODUCT_TOUR_TITLE'),
              description: t('FIRST_STEPS_PRODUCT_TOUR_DESCRIPTION'),
              prevButtonProps: { children: t('PREVIOUS') },
              nextButtonProps: { children: t('NEXT') },
              target: () => document.getElementById(EProductTourTargets.FIRST_STEPS)!,
              checkInDOM: true
            },
            {
              title: t('REDO_THE_TOUR_TITLE'),
              description: t('HERE_YOU_CAN_REDO_THE_TOUR_DESCRIPTION'),
              prevButtonProps: { children: t('PREVIOUS') },
              nextButtonProps: { children: t('FINISH') },
              target: () => document.getElementById(EProductTourTargets.PRODUCT_TOUR_ACTIONS)!
            }
          ]
        },
        '/subscription/:id': {
          tourType: EProductTourType.ACTIVITIES_PRODUCT_TOUR,
          steps: [
            {
              title: t('ACTIVITIES_PRODUCT_TOUR_TITLE'),
              description: t('ACTIVITIES_PRODUCT_TOUR_DESCRIPTION'),
              prevButtonProps: { children: t('SKIP_THE_TOUR') },
              nextButtonProps: {
                children: t('START_THE_TOUR'),
                onClick: () => handleActivitiesNextStep(ESidebarTabs.UMY)
              },

              cover: React.createElement('img', {
                src: UMY_WELCOME,
                style: {
                  width: 147,
                  height: 150,
                  textAlign: 'center'
                }
              })
            },
            {
              title: t('UMY_TAB_PRODUCT_TOUR_TITLE'),
              description: t('UMY_TAB_PRODUCT_TOUR_DESCRIPTION'),
              prevButtonProps: { children: t('PREVIOUS') },
              nextButtonProps: {
                children: t('NEXT'),
                onClick: () => handleActivitiesNextStep(ESidebarTabs.QUESTIONS)
              },
              target: () => document.getElementById(EProductTourTargets.UMY_TAB)!
            },
            {
              title: t('QUESTIONS_TAB_PRODUCT_TOUR_TITLE'),
              description: t('QUESTIONS_TAB_PRODUCT_TOUR_DESCRIPTION'),
              prevButtonProps: {
                children: t('PREVIOUS'),
                onClick: () => handleActivitiesNextStep(ESidebarTabs.UMY)
              },
              nextButtonProps: {
                children: t('NEXT'),
                onClick: () => handleActivitiesNextStep(ESidebarTabs.CONTENT)
              },
              target: () => document.getElementById(EProductTourTargets.QUESTIONS_TAB)!
            },
            {
              title: t('ACTIVITIES_TAB_PRODUCT_TOUR_TITLE'),
              description: t('ACTIVITIES_TAB_PRODUCT_TOUR_DESCRIPTION'),
              prevButtonProps: {
                children: t('PREVIOUS'),
                onClick: () => handleActivitiesNextStep(ESidebarTabs.QUESTIONS)
              },
              nextButtonProps: {
                children: t('NEXT'),
                onClick: () => handleActivitiesNextStep(ESidebarTabs.MY_NOTES)
              },
              target: () => document.getElementById(EProductTourTargets.LESSONS_TAB)!
            },
            {
              title: t('NOTES_TAB_PRODUCT_TOUR_TITLE'),
              description: t('NOTES_TAB_PRODUCT_TOUR_DESCRIPTION'),
              prevButtonProps: {
                children: t('PREVIOUS'),
                onClick: () => handleActivitiesNextStep(ESidebarTabs.CONTENT)
              },
              nextButtonProps: {
                children: t('FINISH'),
                onClick: () =>
                  handleActivitiesNextStep(isMobile ? ESidebarTabs.RESOURCE : ESidebarTabs.UMY)
              },
              target: () => document.getElementById(EProductTourTargets.NOTES_TAB)!
            }
          ]
        },
        [`/subscription/summary?activeTab=${ECourseDetailTabs.CONTENT}`]: {
          tourType: EProductTourType.COURSE_PRODUCT_TOUR,
          steps: [
            {
              title: t('COURSE_PRODUCT_TOUR_WELCOME_TITLE'),
              description: t('COURSE_PRODUCT_TOUR_WELCOME_DESCRIPTION'),
              prevButtonProps: { children: t('SKIP_THE_TOUR') },
              nextButtonProps: { children: t('START_THE_TOUR') },
              cover: React.createElement('img', {
                src: UMY_WELCOME,
                style: {
                  width: 147,
                  height: 150,
                  textAlign: 'center'
                }
              })
            },
            {
              title: t('LESSONS_TAB_PRODUCT_TOUR_TITLE'),
              description: t('LESSONS_TAB_PRODUCT_TOUR_DESCRIPTION'),
              prevButtonProps: { children: t('PREVIOUS') },
              nextButtonProps: { children: t('NEXT') },
              target: () => document.getElementById(EProductTourTargets.LESSONS_TAB)!
            },
            {
              title: t('FAVORITE_LESSONS_TAB_PRODUCT_TOUR_TITLE'),
              description: t('FAVORITE_LESSONS_TAB_PRODUCT_TOUR_DESCRIPTION'),
              prevButtonProps: { children: t('PREVIOUS') },
              nextButtonProps: { children: t('NEXT') },
              target: () => document.getElementById(EProductTourTargets.FAVORITE_LESSONS_TAB)!
            },
            {
              title: t('SECTION_TRACKING_SELECTOR_PRODUCT_TOUR_TITLE'),
              description: t('SECTION_TRACKING_SELECTOR_PRODUCT_TOUR_DESCRIPTION'),
              prevButtonProps: { children: t('PREVIOUS') },
              nextButtonProps: { children: t('NEXT') },
              target: () => document.getElementById(EProductTourTargets.SECTION_TRACKING_SELECTOR)!
            },
            {
              title: t('SECTION_TRACKING_FILTER_PRODUCT_TOUR_TITLE'),
              description: t('SECTION_TRACKING_FILTER_PRODUCT_TOUR_DESCRIPTION'),
              prevButtonProps: { children: t('PREVIOUS') },
              nextButtonProps: { children: t('NEXT') },
              target: () => document.getElementById(EProductTourTargets.SECTION_TRACKING_FILTER)!
            },
            {
              title: t('REDO_THE_TOUR_TITLE'),
              description: t('HERE_YOU_CAN_REDO_THE_TOUR_DESCRIPTION'),
              prevButtonProps: { children: t('PREVIOUS') },
              nextButtonProps: { children: t('FINISH') },
              target: () => document.getElementById(EProductTourTargets.PRODUCT_TOUR_ACTIONS)!
            }
          ]
        },
        '/tests/flashcards/practice/:collectionId': {
          tourType: EProductTourType.FLASHCARDS_PRACTICE_PRODUCT_TOUR,
          steps: [
            {
              title: t('DO_THE_TOUR'),
              prevButtonProps: { children: t('SKIP_THE_TOUR') },
              cover: React.createElement(
                'div',
                {
                  style: {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }
                },
                [
                  React.createElement(Lottie, {
                    key: 'animation',
                    animationData: swipeHandAnimation,
                    style: {
                      width: 150,
                      height: 150
                    }
                  }),
                  React.createElement(
                    'p',
                    {
                      key: 'text',
                      style: {
                        color: '#FFF',
                        textAlign: 'center',
                        fontFamily: 'Poppins',
                        fontSize: 14,
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: '20px',
                        marginTop: 16
                      }
                    },
                    t('SWIPE_TO_CHANGE_CARD')
                  )
                ]
              )
            }
          ]
        }
      };
    }

    if (isTeacher) {
      return {};
    }

    if (isManager) {
      return {};
    }

    return {};
  }, [isStudent, isTeacher, isManager]);

  const pageTour = useMemo(() => {
    const currentPath = pathname;
    const exactMatch = tourSteps[currentPath] ?? tourSteps[currentPath + search];
    if (exactMatch) return exactMatch;

    const pathPattern = Object.keys(tourSteps).find(pattern => {
      if (pattern === '/subscription/:id') {
        const objectIdRegex = /^\/subscription\/[0-9a-f]{24}$/i;
        return objectIdRegex.test(currentPath);
      }
      if (pattern === '/tests/flashcards/practice/:collectionId') {
        const objectIdRegex = /^\/tests\/flashcards\/practice\/[0-9a-f]{24}$/i;
        return objectIdRegex.test(currentPath);
      }
      return pattern === currentPath;
    });

    return pathPattern ? tourSteps[pathPattern] : null;
  }, [tourSteps, pathname, search]);

  const unfinishedPageTour = useMemo(() => {
    if (!pageTour) return null;

    if (!unfinishedTours.includes(pageTour.tourType)) return null;

    const featureFiltered = pageTour.steps.filter(
      step => !step.availableForFeature || canRender(step.availableForFeature)
    );

    const existenceFiltered = featureFiltered.filter(step => {
      if (!step.checkInDOM) return true;

      let targetElement: HTMLElement | null | undefined;
      if (typeof step.target === 'function') {
        const resolver = step.target;
        targetElement = resolver();
      } else {
        targetElement = step.target;
      }
      return !!targetElement;
    });

    if (existenceFiltered.length === 0) return null;

    const adjustedSteps = existenceFiltered.map((step, index, arr) => {
      const isFirst = index === 0;
      const isLast = index === arr.length - 1;

      const prev = step.prevButtonProps ?? {};
      const next = step.nextButtonProps ?? {};

      return {
        ...step,
        prevButtonProps: {
          ...prev,
          children: t(isFirst ? 'SKIP_THE_TOUR' : 'PREVIOUS')
        },
        nextButtonProps: {
          ...next,
          children: t(isLast ? 'FINISH' : 'NEXT')
        }
      };
    });

    return {
      ...pageTour,
      steps: adjustedSteps
    };
  }, [pageTour, unfinishedTours, canRender, t]);

  const tourType = unfinishedPageTour?.tourType;
  const steps = unfinishedPageTour?.steps ?? [];

  const [currentStep, setCurrentStep] = useState(0);
  const [autoScrollOnCurrent, setAutoScrollOnCurrent] = useState(true);
  const [repositionTick, setRepositionTick] = useState(0);
  const scrollTimeoutRef = useRef<number | null>(null);
  const stepTimeoutRef = useRef<number | null>(null);

  const resolvedTargets = useMemo<(HTMLElement | null)[]>(() => {
    return steps.map(step => {
      if (!step || !step.target) return null;
      if (typeof step.target === 'function') {
        try {
          return step.target();
        } catch {
          return null;
        }
      }
      return step.target as HTMLElement;
    });
  }, [steps]);

  const scrollTargetIntoNearestScrollableAncestor = (element: HTMLElement | null) => {
    if (!element) return null;

    let parent: HTMLElement | null = element.parentElement;
    while (parent && parent !== document.body) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      const isScrollable =
        (overflowY === 'auto' || overflowY === 'scroll') &&
        parent.scrollHeight > parent.clientHeight;
      if (isScrollable) {
        const parentRect = parent.getBoundingClientRect();
        const elRect = element.getBoundingClientRect();
        const deltaTop = elRect.top - parentRect.top;
        const currentScrollTop = parent.scrollTop;
        const offset = 80;
        const targetScrollTop = currentScrollTop + deltaTop - offset;
        parent.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'smooth' });
        return parent;
      }
      parent = parent.parentElement;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    return null;
  };

  useEffect(() => {
    if (!unfinishedPageTour) return;
    if (!autoScrollOnCurrent) return;
    const el = resolvedTargets[currentStep] ?? null;
    if (!el) return;
    scrollTargetIntoNearestScrollableAncestor(el);

    if (scrollTimeoutRef.current) {
      window.clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    scrollTimeoutRef.current = window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      window.dispatchEvent(new Event('scroll'));
      setRepositionTick(prevTick => prevTick + 1);
      scrollTimeoutRef.current = null;
    }, DELAY);

    return () => {
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
  }, [unfinishedPageTour, tourType, resolvedTargets, currentStep, autoScrollOnCurrent]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      if (stepTimeoutRef.current) {
        window.clearTimeout(stepTimeoutRef.current);
        stepTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (unfinishedPageTour) {
      setCurrentStep(0);
      setAutoScrollOnCurrent(true);
      setRepositionTick(0);
    }
  }, [tourType, unfinishedPageTour]);

  const handleClose = () => {
    if (!tourType) return;
    if (scrollTimeoutRef.current) {
      window.clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    if (stepTimeoutRef.current) {
      window.clearTimeout(stepTimeoutRef.current);
      stepTimeoutRef.current = null;
    }
    updateProductTour({ tours: { [tourType]: { status: EProductTourStatus.SKIPPED } } });
  };

  const handleFinish = () => {
    if (!tourType) return;
    if (scrollTimeoutRef.current) {
      window.clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    if (stepTimeoutRef.current) {
      window.clearTimeout(stepTimeoutRef.current);
      stepTimeoutRef.current = null;
    }
    updateProductTour({ tours: { [tourType]: { status: EProductTourStatus.COMPLETED } } });
  };

  const handleStepChange = (nextIndex: number) => {
    const el = resolvedTargets[nextIndex] ?? null;
    setAutoScrollOnCurrent(false);
    if (el) scrollTargetIntoNearestScrollableAncestor(el);

    setCurrentStep(nextIndex);
    if (stepTimeoutRef.current) {
      window.clearTimeout(stepTimeoutRef.current);
      stepTimeoutRef.current = null;
    }
    stepTimeoutRef.current = window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      window.dispatchEvent(new Event('scroll'));
      setRepositionTick(prevTick => prevTick + 1);
      setAutoScrollOnCurrent(true);
      stepTimeoutRef.current = null;
    }, DELAY);
  };

  return {
    pageTour,
    unfinishedPageTour,
    tourType,
    steps,
    currentStep,
    repositionTick,
    handleStepChange,
    handleClose,
    handleFinish
  };
};
